import type { FlociAgentRecord } from "../controllers/floci.ts";
import type { AgentsListResult } from "../types.ts";
import type { AgentRosterItem, ChatSubagentCard } from "../ui-types.ts";
import type { SessionLiveness } from "./session-liveness.ts";
import { isSubagentRunActive } from "./subagent-run-status.ts";

export type AgentRosterModelDeps = {
  agentsList: AgentsListResult | null;
  sessionKey: string;
  chatSubagents: ChatSubagentCard[];
  flociAgents?: FlociAgentRecord[];
  /**
   * Pre-computed liveness for the parent session. The caller is
   * responsible for invoking `computeSessionLiveness` once per
   * render so the roster and the chat row never disagree on
   * "is this running?". When omitted, the function falls back to
   * filtering `chatSubagents` itself (kept for backward
   * compatibility with tests that pass only the array).
   */
  liveness?: SessionLiveness;
};

function agentLabel(agent: { id: string; name?: string; identity?: { name?: string } }): string {
  return agent.identity?.name ?? agent.name ?? agent.id;
}

export function buildAgentRoster(deps: AgentRosterModelDeps): AgentRosterItem[] {
  const { agentsList, sessionKey } = deps;
  const registryIds = new Set(agentsList?.agents?.map((agent) => agent.id) ?? []);

  // JAISIU-3337: prefer the pre-computed active-subagent list when
  // the caller supplied one. The filtered list (and `activeRunCount`)
  // already exclude `done`/`error` cards, so the roster can never
  // double-count or report a ghost run. When the caller did not pass
  // `liveness`, fall back to filtering the raw `chatSubagents` array
  // in place using the canonical predicate — same semantics, so the
  // roster can never disagree with the chat row about whether a
  // `cold-start` or `accepted` run counts as live.
  const activeSubagents =
    deps.liveness?.activeSubagents ??
    deps.chatSubagents.filter((card) => isSubagentRunActive(card.status));

  const byId = new Map<string, AgentRosterItem>();

  const parsed = parseAgentSessionKey(sessionKey);
  const mainId = parsed?.agentId ?? agentsList?.defaultId ?? "main";

  const mainItem: AgentRosterItem = {
    id: mainId,
    label: agentsList?.agents?.find((a) => a.id === mainId)
      ? agentLabel(agentsList!.agents.find((a) => a.id === mainId)!)
      : mainId,
    source: "session",
    status: "active",
    workStatus: "idle",
    activeRuns: 0,
  };
  byId.set(mainId, mainItem);

  for (const agent of agentsList?.agents ?? []) {
    if (byId.has(agent.id)) continue;
    byId.set(agent.id, {
      id: agent.id,
      label: agentLabel(agent),
      source: "configured",
      status: "unknown",
      workStatus: "idle",
      activeRuns: 0,
    });
  }

  for (const floci of deps.flociAgents ?? []) {
    if (!registryIds.has(floci.id) || byId.has(floci.id)) {
      continue;
    }
    byId.set(floci.id, {
      id: floci.id,
      label: floci.label?.trim() || floci.id,
      source: "floci",
      status: floci.status === "active" ? "active" : "inactive",
      workStatus: "idle",
      activeRuns: 0,
      lastSeenAt: Date.now(),
    });
  }

  // JAISIU-3337: walk the *filtered* active-subagent list (not the
  // raw array). The previous implementation accumulated `activeRuns`
  // with `existing.activeRuns + 1` per card and never decremented,
  // so a `done` card lingering in `chatSubagents` could keep the
  // count off-by-one — exactly the ghost-run drift operators
  // reported. The new count comes from `activeSubagents.length`
  // per agent, recomputed cleanly each render.
  const activeCountByAgent = new Map<string, number>();
  for (const card of activeSubagents) {
    activeCountByAgent.set(card.agentId, (activeCountByAgent.get(card.agentId) ?? 0) + 1);
  }

  for (const card of activeSubagents) {
    const existing = byId.get(card.agentId);
    if (!existing) {
      if (!registryIds.has(card.agentId)) {
        continue;
      }
      byId.set(card.agentId, {
        id: card.agentId,
        label: card.label,
        source: "subagent",
        status: "active",
        workStatus: card.status,
        activeRuns: activeCountByAgent.get(card.agentId) ?? 1,
        currentTask: card.task,
        currentTool: card.toolHint,
        lastSeenAt: card.updatedAt,
      });
      continue;
    }
    byId.set(card.agentId, {
      ...existing,
      status: "active",
      workStatus: card.status as AgentRosterItem["workStatus"],
      activeRuns: activeCountByAgent.get(card.agentId) ?? existing.activeRuns,
      currentTask: card.task,
      currentTool: card.toolHint,
      lastSeenAt: card.updatedAt ?? existing.lastSeenAt,
    });
  }

  const roster = Array.from(byId.values());
  return roster;
}

function parseAgentSessionKey(key: string): { agentId: string } | null {
  if (!key) return null;
  const parts = key.split(":");
  if (parts.length >= 2 && parts[0] === "agent") {
    return { agentId: parts[1] };
  }
  return null;
}
